import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Server, Users, FileText, Globe, Bell, Clock, Heart, UserCheck, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      icon: Eye,
      title: '1. Personal Information We Collect',
      content: [
        'Identity information: full name, date of birth, ID or passport number (where required for healthcare compliance)',
        'Contact information: email address, phone number, physical address',
        'Account information: username, encrypted password, account preferences',
        'Professional information: practice name, HPCSA registration number (for healthcare professionals)',
        'Financial information: billing details processed securely via PayFast (we do not store card numbers)',
        'Health information (Special Personal Information under POPIA Section 26): patient records, clinical notes, and medical data processed through our platforms on your behalf',
        'Technical information: IP address, browser type, device identifiers, usage logs, and cookies (see our Cookie Policy)',
        'Communication records: emails, support tickets, and correspondence with our team'
      ]
    },
    {
      icon: UserCheck,
      title: '2. Lawful Basis for Processing (POPIA Section 11)',
      content: [
        'Consent (Section 11(1)(a)): Where you have given us explicit consent to process your personal information, including for marketing communications',
        'Contractual necessity (Section 11(1)(b)): Processing necessary to fulfil our service agreement with you (e.g., providing access to VetAxis, StaffAxis, or NoteAxis)',
        'Legal obligation (Section 11(1)(c)): Processing required to comply with South African law, including the National Health Act, HPCSA regulations, and tax legislation',
        'Legitimate interest (Section 11(1)(f)): Processing necessary for our legitimate business interests, such as improving our services, fraud prevention, and security, provided your rights are not overridden',
        'Protection of a legitimate interest of the data subject (Section 11(1)(d)): In emergency situations involving health or safety'
      ]
    },
    {
      icon: Heart,
      title: '3. Special Personal Information (Health Data)',
      content: [
        'Under POPIA Section 26, health information is classified as Special Personal Information requiring additional protections',
        'We process health data only where: (a) you have given explicit consent, (b) processing is necessary for healthcare provision by a healthcare professional bound by confidentiality, or (c) processing is necessary for compliance with healthcare legislation',
        'Patient data entered into NoteAxis, VetAxis, or StaffAxis is processed on your behalf as an Operator (processor) under POPIA, with you as the Responsible Party (controller)',
        'Health data is encrypted at rest and in transit using AES-256 encryption and TLS 1.2+',
        'Access to health data is restricted to authorised personnel through role-based access controls',
        'We maintain processing agreements with all healthcare professional clients in accordance with POPIA Section 20-21'
      ]
    },
    {
      icon: Lock,
      title: '4. How We Use Your Information',
      content: [
        'To provide, operate, and maintain our healthcare software services (VetAxis, StaffAxis, NoteAxis)',
        'To process subscription payments and manage your billing through PayFast',
        'To communicate with you about service updates, maintenance, and security notices',
        'To provide technical support and respond to your enquiries',
        'To improve and personalise our services based on usage patterns (anonymised and aggregated data)',
        'To detect, prevent, and address fraud, security breaches, and technical issues',
        'To comply with legal obligations under South African law, including POPIA, the National Health Act, and SARS requirements',
        'To send marketing communications where you have opted in (you may withdraw consent at any time)'
      ]
    },
    {
      icon: Server,
      title: '5. Data Storage, Security & Retention',
      content: [
        'Your data is stored on Google Firebase / Google Cloud Platform infrastructure with data centres that comply with international security standards (ISO 27001, SOC 2)',
        'All data is encrypted in transit (TLS 1.2+) and at rest (AES-256)',
        'We implement role-based access controls, multi-factor authentication, and audit logging',
        'Regular security assessments and vulnerability testing are conducted',
        'Account data is retained for the duration of your subscription plus 12 months for legitimate business purposes',
        'Financial records are retained for 5 years in accordance with the Tax Administration Act',
        'Health records retention follows the National Health Act requirements (minimum periods as prescribed)',
        'Upon account deletion, personal data is anonymised or securely destroyed within 90 days, subject to legal retention requirements'
      ]
    },
    {
      icon: Users,
      title: '6. Information Sharing & Third Parties',
      content: [
        'We do not sell, trade, or rent your personal information to third parties',
        'PayFast (Pty) Ltd: Payment processing (governed by their privacy policy and PCI DSS compliance)',
        'Google Firebase / Google Cloud Platform: Cloud infrastructure and hosting',
        'EmailJS: Transactional email delivery for contact form submissions',
        'We require all third-party service providers to enter into data processing agreements and maintain appropriate security measures',
        'We may disclose information where required by law, court order, or to protect the rights, safety, or property of BioAxis or others',
        'In the event of a merger, acquisition, or asset sale, your data may be transferred with prior notice to you'
      ]
    },
    {
      icon: Globe,
      title: '7. Cross-Border Data Transfers (POPIA Section 72)',
      content: [
        'Our cloud infrastructure (Google Cloud Platform) may process data in jurisdictions outside South Africa',
        'Cross-border transfers are conducted only where the recipient country has adequate data protection laws, or where appropriate safeguards are in place (binding corporate rules, contractual clauses)',
        'Google Cloud Platform maintains Standard Contractual Clauses and is certified under relevant international frameworks',
        'PayFast processes all payment data within South Africa',
        'You consent to the cross-border transfer of your data as described in this policy when you use our services'
      ]
    },
    {
      icon: FileText,
      title: '8. Your Rights as a Data Subject (POPIA Section 23-25)',
      content: [
        'Right to be informed: Know what personal information we hold about you and how it is processed',
        'Right of access (Section 23): Request a copy of your personal information in our possession',
        'Right to correction (Section 24): Request that inaccurate or incomplete information be corrected or deleted',
        'Right to deletion: Request that your personal information be deleted, subject to legal retention requirements',
        'Right to object (Section 11(3)): Object to the processing of your personal information on reasonable grounds',
        'Right to withdraw consent: Withdraw previously given consent at any time (this does not affect lawfulness of prior processing)',
        'Right to data portability: Request your data in a structured, commonly used, machine-readable format',
        'Right to complain: Lodge a complaint with the Information Regulator (see our POPIA Compliance page for details)',
        'To exercise any of these rights, contact our Information Officer at ' + contactInfo.email.privacy
      ]
    },
    {
      icon: Bell,
      title: '9. Data Breach Notification (POPIA Section 22)',
      content: [
        'In the event of a data breach that poses a risk to your rights, we will notify the Information Regulator as soon as reasonably possible',
        'Affected data subjects will be notified directly via email and/or SMS without undue delay',
        'Our notification will include: the nature of the breach, the information affected, measures taken to address the breach, and recommended steps you should take',
        'We maintain an incident response plan and conduct regular breach simulation exercises',
        'We keep records of all security incidents in accordance with POPIA requirements'
      ]
    },
    {
      icon: Clock,
      title: '10. Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements',
        'Material changes will be communicated via email and/or prominent notice on our website at least 30 days before taking effect',
        'Your continued use of our services after changes take effect constitutes acceptance of the updated policy',
        'Previous versions of this policy are available upon request from our Information Officer'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Shield className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your privacy and data security are our top priorities. This policy explains how BioAxis collects, uses, and protects your personal information in compliance with the Protection of Personal Information Act 4 of 2013 (POPIA).
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026 &middot; Effective date: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6">Overview</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              BioAxis (Pty) Ltd ("BioAxis", "we", "us", or "our"), located in {contactInfo.business.address.full}, is committed to protecting your privacy and ensuring the security of your personal and healthcare data.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our healthcare software solutions, including VetAxis, StaffAxis, NoteAxis, and related services (collectively, the "Services").
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              As a healthcare technology provider, we understand the sensitive nature of medical information. We comply with the <strong>Protection of Personal Information Act 4 of 2013 (POPIA)</strong>, the <strong>Electronic Communications and Transactions Act 25 of 2002 (ECT Act)</strong>, the <strong>National Health Act 61 of 2003</strong>, and other applicable South African legislation.
            </p>
            <div className="bg-primary-900/20 border border-primary-700/30 rounded-xl p-4 mt-6">
              <h3 className="text-lg font-semibold text-primary-300 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Information Officer
              </h3>
              <p className="text-gray-300 text-sm">
                In terms of POPIA Section 55, our designated Information Officer can be contacted at{' '}
                <a href={`mailto:${contactInfo.email.privacy}`} className="text-primary-400 hover:text-primary-300 underline">
                  {contactInfo.email.privacy}
                </a>
                {' '}or by phone at {contactInfo.phone.display}. For full details, see our{' '}
                <Link to="/popia" className="text-primary-400 hover:text-primary-300 underline">POPIA Compliance</Link> page.
              </p>
            </div>
          </motion.div>

          {/* Privacy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="glass-effect glass-effect-hover p-8 rounded-2xl"
              >
                <div className="flex items-start gap-4 mb-6">
                  <section.icon className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                  <h3 className="text-2xl font-bold">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Children's Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12"
          >
            <div className="flex items-start gap-4 mb-6">
              <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">11. Children&apos;s Privacy</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                Our Services are not directed at children under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact our Information Officer immediately.
              </p>
              <p>
                Where healthcare professionals use our platforms to process patient records involving minors, such processing is conducted under the authority and responsibility of the healthcare professional in accordance with the National Health Act and POPIA Section 35 (processing of children&apos;s personal information).
              </p>
            </div>
          </motion.div>

          {/* Related Policies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <h3 className="text-2xl font-bold mb-6">Related Policies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { name: 'POPIA Compliance', href: '/popia', desc: 'Information Officer details & data subject requests' },
                { name: 'Cookie Policy', href: '/cookies', desc: 'How we use cookies and tracking technologies' },
                { name: 'Terms of Service', href: '/terms', desc: 'Terms governing use of our services' },
                { name: 'Acceptable Use Policy', href: '/acceptable-use', desc: 'Rules for using our platforms' },
                { name: 'Refund & Cancellation', href: '/refund-policy', desc: 'Subscription cancellation and refund terms' },
              ].map((policy) => (
                <Link
                  key={policy.href}
                  to={policy.href}
                  className="block p-4 rounded-xl bg-dark-800/50 hover:bg-dark-700/50 border border-dark-700/50 hover:border-primary-500/30 transition-all duration-200"
                >
                  <p className="font-semibold text-primary-400">{policy.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{policy.desc}</p>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Questions About Privacy?</h3>
            <p className="text-gray-300 mb-6">
              If you have any questions about this Privacy Policy, wish to exercise your data subject rights, or want to lodge a complaint, please contact our Information Officer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.privacy}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Information Officer
              </motion.a>
              <Link to="/popia">
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  className="btn-outline inline-block"
                >
                  POPIA Compliance
                </motion.span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
